"use client";

import { createContext, useContext, useSyncExternalStore } from "react";
import { DEFAULT_APPEARANCE, THEME_COLOR, readAppearance, type Appearance } from "@/lib/appearance";

// Must match the inline script in app/layout.tsx that sets the theme before first paint.
const KEY = "chess-atlas-appearance";
const listeners = new Set<() => void>();
let current: Appearance | undefined;

function getAppearance() {
  if (!current) {
    try { current = readAppearance(JSON.parse(localStorage.getItem(KEY) ?? "null")); } catch { current = DEFAULT_APPEARANCE; }
  }
  return current;
}

function subscribeAppearance(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function updateAppearance(patch: Partial<Appearance>) {
  current = { ...getAppearance(), ...patch };
  document.documentElement.dataset.theme = current.theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[current.theme]);
  try { localStorage.setItem(KEY, JSON.stringify(current)); } catch { /* Private browsing still has working controls. */ }
  listeners.forEach((listener) => listener());
}

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
function subscribeReducedMotion(listener: () => void) {
  const media = matchMedia(REDUCED_MOTION);
  media.addEventListener("change", listener);
  return () => media.removeEventListener("change", listener);
}

const AppearanceContext = createContext({ appearance: DEFAULT_APPEARANCE, update: updateAppearance, reducedMotion: false });

export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const appearance = useSyncExternalStore(subscribeAppearance, getAppearance, () => DEFAULT_APPEARANCE);
  const reducedMotion = useSyncExternalStore(subscribeReducedMotion, () => matchMedia(REDUCED_MOTION).matches, () => false);
  return <AppearanceContext.Provider value={{ appearance, update: updateAppearance, reducedMotion }}>{children}</AppearanceContext.Provider>;
}

export const useAppearance = () => useContext(AppearanceContext);

/** A local teaching palette without changing the reader's saved board settings. */
export function LessonAppearance({ children }: { children: React.ReactNode }) {
  const parent = useAppearance();
  return <AppearanceContext.Provider value={{ ...parent, appearance: { ...parent.appearance, board: "sage", pieces: "ivory", style: "staunton" } }}>{children}</AppearanceContext.Provider>;
}

export function ThemeToggle() {
  const { appearance, update } = useAppearance();
  const light = appearance.theme === "light";
  return <button type="button" className="theme-toggle" onClick={() => update({ theme: light ? "dark" : "light" })} aria-label={`Switch to ${light ? "Ember Atelier dark" : "Ivory House light"} theme`} title={light ? "Switch to Ember Atelier" : "Switch to Ivory House"}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>{light ? <path d="M20 15.6A8.6 8.6 0 0 1 8.4 4a8.6 8.6 0 1 0 11.6 11.6Z" /> : <><circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5"/></>}</svg>
  </button>;
}
